<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import copyToClipBoard from '@utils/copy-to-clipboard'
import { siteUrl } from '@/config/site'
import SocialShare from '@components/social-share'
import { useAppearanceStore } from '@state/stores/appearance'
import { useMessages } from '@composables/use-messages'

const props = defineProps({
    genre: {
        type: Object,
        default: () => {},
    },
    hasViewGenreOption: {
        type: Boolean,
        default: true,
    },
    isFollowing: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['toggle-follow'])

const appearanceStore = useAppearanceStore()
const router = useRouter()
const getMessageText = useMessages()

const url = computed(() => {
    // Router 4 throws on a falsy required param, and this resolves during render.
    if (!props.genre.slug) return ''

    const href = router.resolve({ name: 'genre', params: { slug: props.genre.slug } }).href
    return siteUrl(href)
})

function handleToggleFollow() {
    emit('toggle-follow', props.genre)
}

function copyLinkToClipBoard() {
    copyToClipBoard(url.value)
    appearanceStore.handleNotification({
        message: getMessageText('LINK_COPIED_CLIPBOARD'),
        type: 'success',
    })
}
</script>

<template>
    <ul class="genre-options">
        <li
            v-if="hasViewGenreOption"
            class="genre-options-item"
            @click="router.push({ name: 'genre', params: { slug: genre.slug } })"
        >
            <BaseButton
                icon="eye-open"
                size="medium"
                button-type="transparent"
                @click="() => {}"
            >&nbsp; View Genre</BaseButton>
        </li>
        <div
            v-if="hasViewGenreOption"
            class="separator"
        />

        <li class="genre-options-item">
            <BaseButton
                v-if="genre"
                :icon="isFollowing ? 'minus' : 'plus'"
                size="medium"
                button-type="transparent"
                @click="handleToggleFollow"
            >
                {{ isFollowing ? 'Following' : 'Follow Genre' }}
            </BaseButton>
        </li>
        <h6 class="share-title">Share</h6>
        <div class="separator" />
        <li class="genre-options-item">
            <BaseButton
                icon="logo-facebook"
                size="medium"
                button-type="transparent"
            >
                <SocialShare
                    :url="url"
                    type="facebook"
                />
            </BaseButton>
        </li>
        <li class="genre-options-item">
            <BaseButton
                icon="logo-x"
                size="medium"
                button-type="transparent"
            >
                <SocialShare
                    :url="url"
                    :custom-text="`${genre.name} on @friskyradio #feelinfrisky`"
                    type="twitter"
                />
            </BaseButton>
        </li>
        <li
            class="genre-options-item"
            @click="copyLinkToClipBoard"
        >
            <BaseButton
                icon="link"
                size="medium"
                button-type="transparent"
                @click="() => {}"
            >&nbsp;Copy Genre link</BaseButton>
        </li>
    </ul>
</template>

<style lang="scss" scoped>
@import '@design';

.genre-options {
    padding: 0;
    margin: 0;
    text-align: left;
    list-style: none;

    &-item {
        display: flex;
        align-items: center;
        height: 44px;
        padding: 0 $s-m;
    }
}

.separator {
    position: relative;
    display: block;
    width: 100%;
    height: 1px;
    background: $c-surface-overlay;
}

.share-title {
    @extend %contentTitleM;

    margin: $s-s 0 $s-s $s-xl;
}
</style>
